import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle2, XCircle, HelpCircle, FileText, Lightbulb, ArrowRight } from 'lucide-react';
import { useAIOutputReview, ReviewAction, OutputStatus } from '@/hooks/useAIOutputReview';
import { useLocalization } from '@/hooks/useLocalization';

interface StructuredContent {
  facts: string[];
  inference: string;
  recommendation: string;
  confidence: number;
  insufficientEvidence: boolean;
}

interface AIOutputRow {
  id: string;
  title: string;
  status: OutputStatus;
  tenant_id: string | null;
  structured_content: StructuredContent | null;
  payload: Record<string, unknown>;
}

export interface DecisionBriefProps {
  aiOutputId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDecided: () => void;
}

const DecisionBrief = ({ aiOutputId, open, onOpenChange, onDecided }: DecisionBriefProps) => {
  const { tx, t } = useLocalization();
  const { submitting, submitAction } = useAIOutputReview();
  const [output, setOutput] = useState<AIOutputRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<ReviewAction | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open || !aiOutputId) {
      setOutput(null);
      setNotes('');
      setPendingAction(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    supabase
      .from('ai_outputs')
      .select('id, title, status, tenant_id, structured_content, payload')
      .eq('id', aiOutputId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          setError('Failed to load decision brief. Please try again.');
        } else if (!data) {
          setError('Decision brief not found.');
        } else {
          setOutput(data as unknown as AIOutputRow);
        }
        setLoading(false);
      });
  }, [open, aiOutputId]);

  const handleDecide = async (action: ReviewAction) => {
    if (!output) return;
    if ((action === 'reject' || action === 'request_evidence') && !notes.trim()) {
      setPendingAction(action);
      return;
    }
    const result = await submitAction({
      outputId: output.id,
      currentStatus: output.status,
      currentTenantId: output.tenant_id,
      action,
      notes,
    });
    if (result.ok) {
      setNotes('');
      setPendingAction(null);
      onDecided();
    }
  };

  const content = output?.structured_content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Decision Brief')}</DialogTitle>
          <DialogDescription>{tx(output?.title)}</DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="py-8 text-center text-destructive">
            <p>{error}</p>
          </div>
        ) : loading || !content ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : content.insufficientEvidence ? (
          <div className="py-8 text-center text-muted-foreground">
            <p>{t('Insufficient evidence to determine the cause.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4" />  {t('Facts')}
              </h4>
              <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                {content.facts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4" />  {t('Inference')}
              </h4>
              <p className="text-sm text-muted-foreground">{content.inference}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                <ArrowRight className="h-4 w-4" />  {t('Masira Recommendation')}
              </h4>
              <p className="text-sm text-muted-foreground">{content.recommendation}</p>
            </div>
            <Badge variant="outline">{Math.round(content.confidence * 100)}{t('% confidence')}</Badge>
          </div>
        )}

        {pendingAction && (
          <div className="space-y-2 border-t border-border pt-4">
            <label className="text-xs font-medium text-muted-foreground">
              {pendingAction === 'reject' ? t('Reason for rejecting (required)') : t('What evidence is needed (required)')}
            </label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="default" disabled={submitting} onClick={() => handleDecide('approve')}>
            <CheckCircle2 className="h-4 w-4 me-1" />  {t('Approve')}
          </Button>
          <Button variant="outline" disabled={submitting} onClick={() => handleDecide('correct')}>
            
            {t('Modify')}
          </Button>
          <Button variant="outline" disabled={submitting} onClick={() => handleDecide('request_evidence')}>
            <HelpCircle className="h-4 w-4 me-1" />  {t('Request More Evidence')}
          </Button>
          <Button variant="destructive" disabled={submitting} onClick={() => handleDecide('reject')}>
            <XCircle className="h-4 w-4 me-1" />  {t('Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DecisionBrief;
